if (!customElements.get('image-slider')) {
  customElements.define('image-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
      this.status = this.querySelector('[data-slider-status]');
      this.previousButton = this.querySelector('[data-slider-previous]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.index = 0;
      this.timer = null;
      this.pointerStart = null;
      this.suppressClick = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;
      this.interval = Math.max(3000, Number(this.dataset.interval) || 3500);

      if (!this.track || !this.slides.length) return;

      if (this.slides.length > 1) {
        this.clone = this.slides[0].cloneNode(true);
        this.clone.setAttribute('aria-hidden', 'true');
        this.clone.removeAttribute('data-slider-slide');
        this.clone.removeAttribute('data-block-id');
        this.clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        this.track.appendChild(this.clone);
      }

      this.previousButton?.addEventListener('click', () => {
        this.stop();
        this.previous();
      });
      this.nextButton?.addEventListener('click', () => {
        this.stop();
        this.next();
      });
      this.addEventListener('keydown', event => this.onKeyDown(event));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.addEventListener('pointerdown', event => this.onPointerDown(event));
      this.addEventListener('pointerup', event => this.onPointerUp(event));
      this.addEventListener('pointercancel', () => { this.pointerStart = null; this.start(); });
      this.addEventListener('click', event => {
        if (!this.suppressClick) return;
        event.preventDefault();
        this.suppressClick = false;
      }, true);
      this.track.addEventListener('transitionend', () => this.onTransitionEnd());
      document.addEventListener('visibilitychange', () => document.hidden ? this.stop() : this.start());

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', event => this.selectBlock(event));
        this.addEventListener('shopify:block:deselect', () => this.start());
      }

      this.update(false);
      requestAnimationFrame(() => this.classList.add('is-ready'));
      this.start();
    }

    disconnectedCallback() { this.stop(); }

    start() {
      this.stop();
      if (!this.autoplay || this.slides.length < 2 || document.hidden) return;
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    next() {
      if (this.index >= this.slides.length) return;
      this.index += 1;
      this.update(true);
    }

    previous() {
      if (this.index === 0) {
        this.track.classList.add('is-resetting');
        this.index = this.slides.length;
        this.update(false);
        this.track.getBoundingClientRect();
        this.track.classList.remove('is-resetting');
      }
      this.index -= 1;
      this.update(true);
    }

    update(announce) {
      const visible = this.index === this.slides.length ? 0 : this.index;
      this.track.style.transform = `translate3d(-${this.index * 100}%,0,0)`;
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === visible);
        slide.setAttribute('aria-hidden', index === visible ? 'false' : 'true');
      });
      if (this.clone) this.clone.classList.toggle('is-active', this.index === this.slides.length);
      if (announce && this.status) this.status.textContent = `Slide ${visible + 1} of ${this.slides.length}`;
    }

    onTransitionEnd() {
      if (this.index !== this.slides.length) return;
      this.track.classList.add('is-resetting');
      this.index = 0;
      this.update(false);
      this.track.getBoundingClientRect();
      this.track.classList.remove('is-resetting');
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('[data-slider-previous],[data-slider-next]')) return;
      this.pointerStart = event.clientX;
      this.stop();
    }

    onKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.stop();
      event.key === 'ArrowLeft' ? this.previous() : this.next();
    }

    onPointerUp(event) {
      if (this.pointerStart === null) return;
      const distance = event.clientX - this.pointerStart;
      this.pointerStart = null;
      if (Math.abs(distance) > 45) {
        this.suppressClick = true;
        distance < 0 ? this.next() : this.previous();
      }
      this.start();
    }

    selectBlock(event) {
      const slide = event.target.closest('[data-slider-slide]');
      if (!slide) return;
      const selected = this.slides.indexOf(slide);
      if (selected < 0) return;
      this.stop();
      this.index = selected;
      this.update(false);
    }
  });
}
